/**
 * Header - App header with branding and controls
 */

import { Sun, Moon, X } from 'lucide-react'

/**
 * MoziLogo - Mozi "M" symbol icon
 */
function MoziLogo({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 1024 1024"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g transform="translate(93, 208)">
        <path
          d="M343.484199,5.73289404 C357.102917,15.4979078 366.953036,27.6758354 367.955839,44.9386955 C367.955839,45.6026517 368.489589,207.073551 368.473415,208.773926 C368.586635,217.615879 368.942468,222.911334 375.832699,229.210821 C382.91702,233.826126 388.998538,233.24314 396.859224,230.830226 C407.011181,224.138082 601.673751,26.5098636 603.42057,24.7285178 C617.96122,9.86237747 630.544787,-0.728532963 652.121236,0.194528038 C669.07185,2.44550136 680.620265,10.4939455 689.483754,24.9066524 C694.643339,35.254652 694.918302,44.3880977 694.885953,55.7239346 C694.885953,56.5984134 695.225612,205.195041 695.209438,206.879222 C695.322658,216.449907 696.212242,221.907303 702.552548,229.210821 C709.361907,233.92329 715.912478,233.032617 723.579073,230.830226 C726.684529,228.676417 726.684529,228.676417 729.676765,225.939622 C730.776614,224.919397 746.967038,208.919673 753.727874,201.761901 C767.006934,188.142703 780.415387,181.891799 799.598047,183.867473 C817.3412,188.660913 826.867833,198.620255 835.181397,214.636173 C837.025262,220.190733 837,224.433575 837,230.279628 C837,231.380824 836.798822,390.341645 836.798822,449.449937 C836.798822,508.558229 739.753323,585.998189 739.753323,587.099384 C723.95108,601.398733 711.610128,609.997775 689.66167,607.601055 C675.412156,604.475603 663.718174,594.856335 657.086731,581.998258 C652.541767,571.407347 651.490441,562.354872 651.587486,550.905677 C651.571312,550.015004 651.571312,550.015004 651.538963,545.561639 C651.522789,539.796556 651.231653,399.296956 651.199304,395.475159 C651.199304,394.163441 651.199304,392.851723 651.199304,391.491422 C650.53616,385.677757 648.659947,382.374171 644.228202,378.600956 C636.917441,375.491698 625.72486,374.147592 621.551904,377.872224 C617.637735,381.434916 639.084791,360.382647 577.816732,422.324899 C516.548673,484.26715 431.633861,571.715034 419.503174,582.241168 C407.372486,592.767303 398.492823,602.257018 385.537249,606.532247 C366.483982,608.475534 351.118445,606.904711 337.20859,592.459616 C327.2776,579.633926 324.738243,566.840624 324.867637,550.905677 C324.851463,549.140525 324.479455,392.851723 324.479455,391.491422 C323.816311,385.677757 323.541349,379.443047 316.796686,376.5767 C310.052024,373.710352 306.283424,373.337889 298.196299,376.5767 C290.109174,379.81551 280.016442,391.086571 272.010188,399.248373 C270.910339,400.365763 91.9746122,580.621763 82.7876382,589.236999 C68.1404582,602.972633 59.0923621,608.184041 39.2789059,607.244786 C24.1559822,605.54441 13.917682,597.933205 5.44237498,585.479979 C1.12385025,578.063103 0,572.751454 0,564.200994 C0,563.083604 0.315137748,420.219672 0.347486248,403.75032 C0.525402997,283.525673 30.7550761,271.655433 111.6425,190.474647 C116.025722,186.086058 282.474928,18.8338826 283.590951,17.6841049 C299.959292,1.55482846 322.118015,-6.07257034 343.484199,5.73289404 Z"
          fill="currentColor"
          fillRule="nonzero"
        />
      </g>
    </svg>
  )
}

interface HeaderProps {
  hasSession: boolean
  sessionTitle?: string
  isDark: boolean
  onToggleTheme: () => void
  onClear: () => void
}

export function Header({ hasSession, sessionTitle, isDark, onToggleTheme, onClear }: HeaderProps) {
  return (
    <header className="shrink-0 grid grid-cols-[auto_1fr_auto] items-center px-4 py-3">
      {/* Logo - links to main site */}
      <a
        href="https://s.mozi.chat"
        className="hover:opacity-80 transition-opacity"
        title="Mozi"
      >
        <MoziLogo className="w-6 h-6 text-[#9570BE]" />
      </a>

      {/* Session title - centered */}
      <div className="flex justify-center">
        {sessionTitle && (
          <span className="text-sm font-semibold text-foreground truncate max-w-md">
            {sessionTitle}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        {/* Clear button (when session is loaded) */}
        {hasSession && (
          <button
            onClick={onClear}
            className="p-1.5 rounded-md bg-background shadow-minimal text-foreground/40 hover:text-foreground/70 transition-colors"
            title="Clear session"
          >
            <X className="w-4 h-4" />
          </button>
        )}

        {/* Theme toggle */}
        <button
          onClick={onToggleTheme}
          className="p-1.5 rounded-md bg-background shadow-minimal text-foreground/40 hover:text-foreground/70 transition-colors"
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </button>
      </div>
    </header>
  )
}
